import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Input,
  OnInit,
  signal,
  inject
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Admin, GlobalEventsService, NavigationLink, RouteBuilder, RouteTree } from '@coin/shared/core/util-helpers';
import { PermissionsService } from '@coin/shared/auth/feature';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'coin-a-navigation-menu',
  templateUrl: './navigation-menu.component.html',
  styleUrls: ['./navigation-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class NavigationMenuComponent implements OnInit {
  private eRef = inject(ElementRef);
  private events = inject(GlobalEventsService);
  private router = inject(Router);
  private permissionsService = inject(PermissionsService);
  private activatedRoute = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);

  @Input() disableNavigationLink: (link: NavigationLink) => boolean;
  public isMenuVisible = signal(false);
  public navigationLinks = signal<NavigationLink[]>([]);

  clickout = (event: MouseEvent): void => {
    if (!this.eRef.nativeElement.contains(event.target)) {
      if (this.isMenuVisible()) {
        this.onToggleMenu();
      }
    }
  };

  ngOnInit(): void {
    this.events
      .listen('click')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => this.clickout(event));
    this.initNavigationLinks();
  }

  public onToggleMenu(): void {
    this.isMenuVisible.update(isVisible => !isVisible);
  }

  private initNavigationLinks(): void {
    const url = new URL(window.location.href);
    this.navigationLinks.set(
      this.permissionsService.getNavigationLinks(url, Admin as unknown as RouteBuilder<RouteTree>)
    );
  }

  public navigate(link: NavigationLink): void {
    if (!this.disableNavigationLink(link)) {
      this.activatedRoute.queryParamMap
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(queryParams => {
          const params = {};
          if (link.preserveParams) {
            for (const preverseParamKey of link.preserveParams) {
              params[preverseParamKey] = queryParams.get(preverseParamKey);
            }
          }

          const url = this.router.createUrlTree([link.url], { queryParams: params });
          this.router.navigateByUrl(url);
        })
        .unsubscribe();
    }
  }

  // pure fn
  public isSelected(link: NavigationLink): boolean {
    return new URL(window.location.href).pathname.startsWith(link.url);
  }

  public getLinkTooltip = (link: NavigationLink): string =>
    this.disableNavigationLink(link) ? link.disabledTooltip : link.title;
}
